import { useCallback, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import useInlineEdit from '../components/admin/useInlineEdit';
import { DocumentMapCategory, LevelType, TabCategory, Subcategory, TabItem, TemplateInfo } from '../types/AdminTypes';
import { FieldTypeRow } from '../components/admin/FieldTypeTable';
import { DocumentTypeRow } from '../components/admin/DocumentTypeTable';

// ── Hook ───────────────────────────────────────────────────────────────────

const useAdminDocumentMap = (adminToken: string | null) => {
  // ── Fetch state ──────────────────────────────────────────────────────
  const [documentMap, setDocumentMap] = useState<DocumentMapCategory[]>([]);
  const [documentMapLoading, setDocumentMapLoading] = useState(false);
  const [documentMapError, setDocumentMapError] = useState<string | null>(null);
  const documentMapLoaded = useRef(false);

  // ── Template state ───────────────────────────────────────────────────
  // Map from filing_document_type id → template info
  const [templatesMap, setTemplatesMap] = useState<Record<string, TemplateInfo>>({});

  // ── UI state ─────────────────────────────────────────────────────────
  const [level, setLevel] = useState<LevelType>('employee');
  const [selectedCatId, setSelectedCatId] = useState<string | null>(null);
  const [selectedSubcat, setSelectedSubcat] = useState<Subcategory | null>(null);
  const [catTab, setCatTab] = useState<TabCategory>('subcategories');
  const [subcatTab, setSubcatTab] = useState<TabItem>('documentTypes');
  const catEdit = useInlineEdit();
  const docTypeEdit = useInlineEdit();
  const fieldTypeEdit = useInlineEdit();

  // ── Derived ──────────────────────────────────────────────────────────
  const filteredCats = documentMap.filter(c => c.type === level);
  const selectedCat = filteredCats.find(c => c.id === selectedCatId) ?? null;

  // Clear selection when level changes and previously selected cat is no longer visible
  useEffect(() => {
    if (selectedCat === null) { setSelectedCatId(null); setSelectedSubcat(null); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  // Keep selectedSubcat in sync with documentMap after mutations
  useEffect(() => {
    if (!selectedSubcat || !selectedCat) return;
    const fresh = (selectedCat.subcategories as Subcategory[] | undefined)?.find(sc => sc.id === selectedSubcat.id);
    if (fresh) setSelectedSubcat(fresh as Subcategory);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentMap]);

  // ── Navigation ───────────────────────────────────────────────────────
  const selectCategory = (id: string) => {
    if (id !== selectedCatId) {
      setSelectedCatId(id);
      setSelectedSubcat(null);
      setCatTab('subcategories');
    }
  };

  const drillIntoSubcat = (subcat: Subcategory) => {
    setSelectedSubcat(subcat);
    setSubcatTab('documentTypes');
  };

  const exitSubcat = () => setSelectedSubcat(null);

  // ── API helpers ──────────────────────────────────────────────────────
  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${adminToken}` }),
    [adminToken]
  );

  const updateCat = (catId: string, updater: (cat: DocumentMapCategory) => DocumentMapCategory) => {
    setDocumentMap(prev => prev.map(c => c.id === catId ? updater(c) : c));
  };

  const mutate = async (fn: () => Promise<void>) => {
    try {
      await fn();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      window.alert(`Error: ${message}`);
    }
  };

  // ── Fetch ────────────────────────────────────────────────────────────
  const fetchDocumentMap = useCallback(async () => {
    const token = sessionStorage.getItem('adminToken');
    if (!token || documentMapLoaded.current) return;
    documentMapLoaded.current = true;
    setDocumentMapLoading(true);
    setDocumentMapError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/document_map`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocumentMap(res.data.categories || []);
    } catch (err: any) {
      documentMapLoaded.current = false;
      setDocumentMapError(err?.response?.data?.message || err?.message || 'Failed to load document map');
    } finally {
      setDocumentMapLoading(false);
    }
  }, []);

  const resetDocumentMap = useCallback(() => {
    setDocumentMap([]);
    setDocumentMapError(null);
    documentMapLoaded.current = false;
    setLevel('employee');
    setSelectedCatId(null);
    setSelectedSubcat(null);
    setCatTab('subcategories');
    setSubcatTab('documentTypes');
  }, []);

  // ── Category CRUD ────────────────────────────────────────────────────
  const addCategory = async (name: string) => mutate(async () => {
    const res = await axios.post(`${API_BASE_URL}/api/admin/categories`, { name, type: level }, { headers: authHeaders() });
    const newCat = { ...res.data, subcategories: [], documentTypes: [], fields: [] };
    setDocumentMap(prev => [...prev, newCat]);
  });

  const renameCategory = async (id: string) => mutate(async () => {
    await axios.patch(`${API_BASE_URL}/api/admin/categories/${id}`, { name: catEdit.editValue }, { headers: authHeaders() });
    updateCat(id, c => ({ ...c, name: catEdit.editValue }));
    catEdit.cancel();
  });

  const deleteCategory = async (id: string) => {
    if (!window.confirm('Delete this category and all its contents?')) return;
    await mutate(async () => {
      await axios.delete(`${API_BASE_URL}/api/admin/categories/${id}`, { headers: authHeaders() });
      setDocumentMap(prev => prev.filter(c => c.id !== id));
      if (selectedCatId === id) { setSelectedCatId(null); setSelectedSubcat(null); }
    });
  };

  // ── Subcategory CRUD ─────────────────────────────────────────────────
  const addSubcategory = async (name: string) => {
    if (!selectedCatId) return;
    await mutate(async () => {
      const res = await axios.post(`${API_BASE_URL}/api/admin/subcategories`, { name, category_id: selectedCatId }, { headers: authHeaders() });
      const newSub = { ...res.data, documentTypes: [], fields: [] };
      updateCat(selectedCatId, c => ({ ...c, subcategories: [...(c.subcategories || []), newSub] }));
    });
  };

  const renameSubcategory = async (id: string, name: string) => mutate(async () => {
    await axios.patch(`${API_BASE_URL}/api/admin/subcategories/${id}`, { name }, { headers: authHeaders() });
    if (selectedCatId) {
      updateCat(selectedCatId, c => ({
        ...c,
        subcategories: (c.subcategories || []).map((sc: any) => sc.id === id ? { ...sc, name } : sc),
      }));
    }
  });

  const deleteSubcategory = async (id: string) => {
    if (!window.confirm('Delete this subcategory?')) return;
    await mutate(async () => {
      await axios.delete(`${API_BASE_URL}/api/admin/subcategories/${id}`, { headers: authHeaders() });
      if (selectedCatId) {
        updateCat(selectedCatId, c => ({
          ...c,
          subcategories: (c.subcategories || []).filter((sc: any) => sc.id !== id),
        }));
      }
      if (selectedSubcat?.id === id) setSelectedSubcat(null);
    });
  };

  // ── Document type CRUD ───────────────────────────────────────────────
  const addDocumentType = async (name: string, recurrence: string, multi_allowed: string, viewingRole: string, edittingRole: string, catId: string, subcatId?: string) => mutate(async () => {
    const res = await axios.post(`${API_BASE_URL}/api/admin/document_types`, {
      name, category_id: catId, subcategory_id: subcatId ?? null, multi_uploads: multi_allowed, recurrence: recurrence, viewingRole: viewingRole, edittingRole: edittingRole
    }, { headers: authHeaders() });
    const newDt = res.data;
    updateCat(catId, c => {
      if (subcatId) {
        return {
          ...c,
          subcategories: (c.subcategories || []).map((sc: any) =>
            sc.id === subcatId ? { ...sc, documentTypes: [...(sc.documentTypes || []), newDt] } : sc
          ),
        };
      }
      return { ...c, documentTypes: [...(c.documentTypes || []), newDt] };
    });
    if (subcatId && selectedSubcat?.id === subcatId) {
      setSelectedSubcat(prev => prev ? { ...prev, documentTypes: [...(prev.documentTypes || []), newDt] } : prev);
    }
  });

  const updateDocumentType = async (row: DocumentTypeRow, catId: string, subcatId?: string) => mutate(async () => {
    await axios.patch(`${API_BASE_URL}/api/admin/document_types/${row.id}`, { name: row.name, recurrence: row.recurrence, multi_uploads: row.multi_uploads, viewingRole: row.viewingRole, edittingRole: row.edittingRole }, { headers: authHeaders() });
    updateCat(catId, c => {
      if (subcatId) {
        return {
          ...c,
          subcategories: (c.subcategories || []).map((sc: any) =>
            sc.id === subcatId
              ? { ...sc, documentTypes: (sc.documentTypes || []).map((dt: any) => dt.id === row.id ? { ...dt, ...row } : dt) }
              : sc
          ),
        };
      }
      return { ...c, documentTypes: (c.documentTypes || []).map((dt: any) => dt.id === row.id ? { ...dt, ...row } : dt) };
    });
    if (subcatId && selectedSubcat?.id === subcatId) {
      setSelectedSubcat(prev => prev ? { ...prev, documentTypes: (prev.documentTypes || []).map((dt: any) => dt.id === row.id ? { ...dt, ...row } : dt) } : prev);
    }
  });

  const deleteDocumentType = async (id: string, catId: string, subcatId?: string) => {
    if (!window.confirm('Delete this document type?')) return;
    await mutate(async () => {
      await axios.delete(`${API_BASE_URL}/api/admin/document_types/${id}`, { headers: authHeaders() });
      updateCat(catId, c => {
        if (subcatId) {
          return {
            ...c,
            subcategories: (c.subcategories || []).map((sc: any) =>
              sc.id === subcatId
                ? { ...sc, documentTypes: (sc.documentTypes || []).filter((dt: any) => dt.id !== id) }
                : sc
            ),
          };
        }
        return { ...c, documentTypes: (c.documentTypes || []).filter((dt: any) => dt.id !== id) };
      });
    });
  };

  // ── Field type CRUD ──────────────────────────────────────────────────
  const addFieldType = async (name: string, value_type: string, required: boolean, viewingRole: string, edittingRole: string, catId: string, subcatId?: string) => mutate(async () => {
    const res = await axios.post(`${API_BASE_URL}/api/admin/field_types`, {
      name, category_id: catId, subcategory_id: subcatId ?? null, value_type, required, viewingRole, edittingRole,
    }, { headers: authHeaders() });
    const newFt = res.data;
    updateCat(catId, c => {
      if (subcatId) {
        return {
          ...c,
          subcategories: (c.subcategories || []).map((sc: any) =>
            sc.id === subcatId ? { ...sc, fieldTypes: [...(sc.fieldTypes || []), newFt] } : sc
          ),
        };
      }
      return { ...c, fieldTypes: [...(c.fieldTypes || []), newFt] };
    });
  });

  const updateFieldType = async (row: FieldTypeRow, catId: string, subcatId?: string) => mutate(async () => {
    await axios.patch(`${API_BASE_URL}/api/admin/field_types/${row.id}`, { name: row.name, value_type: row.value_type, required: row.required, viewingRole: row.viewingRole, edittingRole: row.edittingRole }, { headers: authHeaders() });
    updateCat(catId, c => {
      if (subcatId) {
        return {
          ...c,
          subcategories: (c.subcategories || []).map((sc: any) =>
            sc.id === subcatId
              ? { ...sc, fieldTypes: (sc.fieldTypes || []).map((dt: any) => dt.id === row.id ? { ...dt, ...row } : dt) }
              : sc
          ),
        };
      }
      return { ...c, fieldTypes: (c.fieldTypes || []).map((dt: any) => dt.id === row.id ? { ...dt, ...row } : dt) };
    });
    if (subcatId && selectedSubcat?.id === subcatId) {
      setSelectedSubcat(prev => prev ? { ...prev, fieldTypes: (prev.fieldTypes || []).map((dt: any) => dt.id === row.id ? { ...dt, ...row } : dt) } : prev);
    }
  });

  const deleteFieldType = async (id: string, catId: string, subcatId?: string) => {
    if (!window.confirm('Delete this field type?')) return;
    await mutate(async () => {
      await axios.delete(`${API_BASE_URL}/api/admin/field_types/${id}`, { headers: authHeaders() });
      updateCat(catId, c => {
        if (subcatId) {
          return {
            ...c,
            subcategories: (c.subcategories || []).map((sc: any) =>
              sc.id === subcatId
                ? { ...sc, fieldTypes: (sc.fieldTypes || []).filter((f: any) => f.id !== id) }
                : sc
            ),
          };
        }
        return { ...c, fieldTypes: (c.fieldTypes || []).filter((f: any) => f.id !== id) };
      });
    });
  };

  // ── Template management ──────────────────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    if (!adminToken) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/templates/admin/all`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const list: any[] = res.data.templates ?? res.data ?? [];
      const map: Record<string, TemplateInfo> = {};
      for (const t of list) {
        const dtId: string | undefined =
          typeof t.documentTypeId === 'object' ? t.documentTypeId?._id : t.documentTypeId;
        if (dtId) map[dtId] = { _id: t._id, doc_name: t.doc_name, fileType: t.fileType };
      }
      setTemplatesMap(map);
    } catch {
      // templates are optional — silently ignore
    }
  }, [adminToken]);

  const uploadTemplate = (file: File, documentTypeId: string, categoryId: string, subcategoryId?: string) =>
    mutate(async () => {
      let s3Key: string | undefined;
      try {
        // Step 1: upload the file to storage
        const fd = new FormData();
        fd.append('file', file);
        const uploadRes = await axios.post(`${API_BASE_URL}/api/templates/upload`, fd, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        const { fileUrl, s3Key: uploadedKey, fileType, fileSize } = uploadRes.data;
        s3Key = uploadedKey;

        // Step 2: create the template record linked to the document type
        const title = file.name.replace(/\.[^/.]+$/, '');
        await axios.post(`${API_BASE_URL}/api/templates`, {
          title, fileUrl, s3Key, fileType, fileSize,
          documentTypeId, categoryId, subcategoryId: subcategoryId ?? null,
          isActive: true,
        }, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });

        await fetchTemplates();
      } catch (err: any) {
        const message = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to upload template';
        throw new Error(message);
      }
    });

  const downloadAdminTemplate = async (templateId: string) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/templates/download/${templateId}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob',
      });
      const disposition: string = res.headers['content-disposition'] ?? '';
      const match = disposition.match(/filename[^;=\n]*=\s*['"]?([^'"\n;]+)['"]?/i);
      const filename = match?.[1] ?? 'template';
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', filename);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.alert('Failed to download template.');
    }
  };

  const deleteTemplate = (templateId: string, documentTypeId: string) => {
    if (!window.confirm('Remove this template?')) return Promise.resolve();
    return mutate(async () => {
      await axios.delete(`${API_BASE_URL}/api/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setTemplatesMap(prev => {
        const next = { ...prev };
        delete next[documentTypeId];
        return next;
      });
    });
  };

  // Fetch templates once the document map has loaded
  useEffect(() => {
    if (adminToken && documentMap.length > 0) {
      fetchTemplates();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, documentMap.length]);

  return {
    documentMap,
    documentMapLoading,
    documentMapError,
    fetchDocumentMap,
    resetDocumentMap,
    level,
    setLevel,
    filteredCats,
    selectedCat,
    selectedCatId,
    selectedSubcat,
    selectCategory,
    drillIntoSubcat,
    exitSubcat,
    catTab,
    setCatTab,
    subcatTab,
    setSubcatTab,
    catEdit,
    docTypeEdit,
    fieldTypeEdit,
    addCategory,
    renameCategory,
    deleteCategory,
    addSubcategory,
    renameSubcategory,
    deleteSubcategory,
    addDocumentType,
    updateDocumentType,
    deleteDocumentType,
    addFieldType,
    updateFieldType,
    deleteFieldType,
    templatesMap,
    fetchTemplates,
    uploadTemplate,
    downloadAdminTemplate,
    deleteTemplate,
  };
};

export default useAdminDocumentMap;
